// Phase 9 R2 — Create inventory item dialog (14 fields).
import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useCreateInventoryItem } from '@/hooks/inventory/useCreateInventoryItem';

const schema = z.object({
  part_number: z.string().trim().min(1, 'Part number is required'),
  brand: z.string().trim().optional(),
  name: z.string().trim().min(1, 'Name is required'),
  description: z.string().trim().optional(),
  category: z.string().trim().optional(),
  supplier: z.string().trim().optional(),
  supplier_part_number: z.string().trim().optional(),
  unit_cost: z.coerce.number().min(0, 'Must be ≥ 0'),
  unit_sell_default: z.coerce.number().min(0).optional().or(z.literal('').transform(() => undefined)),
  reorder_point: z.coerce.number().min(0).default(0),
  reorder_quantity: z.coerce.number().min(0).default(0),
  bin_location: z.string().trim().optional(),
  notes: z.string().trim().optional(),
  initial_quantity: z.coerce.number().min(0).default(0),
});

type FormValues = z.infer<typeof schema>;

export function CreateInventoryItemDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const create = useCreateInventoryItem();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      part_number: '',
      name: '',
      unit_cost: 0,
      reorder_point: 0,
      reorder_quantity: 0,
      initial_quantity: 0,
    },
  });

  const submit = async (values: FormValues) => {
    await create.mutateAsync({
      part_number: values.part_number,
      brand: values.brand || null,
      name: values.name,
      description: values.description || null,
      category: values.category || null,
      supplier: values.supplier || null,
      supplier_part_number: values.supplier_part_number || null,
      unit_cost: values.unit_cost,
      unit_sell_default: values.unit_sell_default ?? null,
      reorder_point: values.reorder_point,
      reorder_quantity: values.reorder_quantity,
      bin_location: values.bin_location || null,
      notes: values.notes || null,
      initial_quantity: values.initial_quantity,
    });
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Inventory Item</DialogTitle>
          <DialogDescription>
            Add a new stocked part. If initial quantity is greater than zero, an{' '}
            <code>initial_load</code> ledger entry is recorded.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField name="part_number" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Part Number *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="brand" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Brand</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField name="name" control={form.control} render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Name *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="description" control={form.control} render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Description</FormLabel>
                  <FormControl><Textarea rows={2} {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField name="category" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField name="supplier" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Supplier</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField name="supplier_part_number" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Supplier Part #</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField name="bin_location" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Bin Location</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField name="unit_cost" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit Cost *</FormLabel>
                  <FormControl><Input type="number" step="0.01" min="0" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField name="unit_sell_default" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Sell</FormLabel>
                  <FormControl><Input type="number" step="0.01" min="0" {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField name="reorder_point" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Reorder Point</FormLabel>
                  <FormControl><Input type="number" step="1" min="0" {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField name="reorder_quantity" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>Reorder Quantity</FormLabel>
                  <FormControl><Input type="number" step="1" min="0" {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField name="initial_quantity" control={form.control} render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Initial Quantity</FormLabel>
                  <FormControl><Input type="number" step="1" min="0" {...field} /></FormControl>
                </FormItem>
              )} />
              <FormField name="notes" control={form.control} render={({ field }) => (
                <FormItem className="md:col-span-2">
                  <FormLabel>Notes</FormLabel>
                  <FormControl><Textarea rows={2} {...field} /></FormControl>
                </FormItem>
              )} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Adding…' : 'Add Item'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
